import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import { fetchEnterprises, createEnterprise } from "../../Utils/functions";
import "./Enterprise.css";

function Enterprise() {
  const navigate = useNavigate();
  const [enterprises, setEnterprises] = useState([]);
  const [loading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newEnterpriseName, setNewEnterpriseName] = useState("");
  const [newEnterpriseCapacity, setNewEnterpriseCapacity] = useState("");
  const [newExpiryDate, setNewExpiryDate] = useState("");
  const [creating, setCreating] = useState(false);
  const showCenterButton = !loading && enterprises.length === 0;

  useEffect(() => {
    const fetchEnterprisesList = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchEnterprises();
        setEnterprises(data || []);
      } catch (error) {
        setError(
          error.message || "Failed to fetch enterprises. Please try again."
        );
        console.error("Error fetching enterprises:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchEnterprisesList();
  }, []);

  const handleEnterpriseClick = (enterpriseId) => {
    navigate(`/enterprise/${enterpriseId}`);
  };

  const handleCreateEnterpriseClick = () => {
    setCreateDialogOpen(true);
  };

  const handleCreateEnterprise = async () => {
    if (!newEnterpriseName.trim()) {
      setError("Enterprise Name required.");
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const newEnterprise = await createEnterprise(
        newEnterpriseName,
        newEnterpriseCapacity ? parseInt(newEnterpriseCapacity) : null,
        newExpiryDate || null
      );
      setEnterprises([...enterprises, newEnterprise]);
      setCreateDialogOpen(false);
      setNewEnterpriseName("");
      setNewEnterpriseCapacity("");
      setNewExpiryDate("");
      setSnackbar({
        open: true,
        message: "Enterprise created successfully!",
        severity: "success",
      });
    } catch (error) {
      setError(
        error.message || "Failed to create enterprise. Please try again."
      );
      console.error("Error creating enterprise:", error);
    } finally {
      setCreating(false);
    }
  };

  const handleCloseDialog = () => {
    setCreateDialogOpen(false);
    setNewEnterpriseName("");
    setNewEnterpriseCapacity("");
    setNewExpiryDate("");
    setError(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <div>
      <Box
        sx={{
          display: "flex",
          justifyContent: showCenterButton ? "center" : "flex-end",
          mb: 2,
          width: "100%",
          pt: showCenterButton ? "12%" : 0,
        }}
      >
        <Button
          variant="contained"
          onClick={handleCreateEnterpriseClick}
          sx={{
            backgroundColor: " #49994C",
            "&:hover": { backgroundColor: "darkgreen" },
          }}
        >
          Create Enterprise
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        enterprises.length > 0 && (
          <TableContainer
            component={Paper}
            className="enterprise-table-container"
          >
            <Table sx={{ minWidth: 650 }} aria-label="enterprises table">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Company Name</strong>
                    <br />
                    <span></span>
                  </TableCell>
                  <TableCell>
                    <strong>Capacity</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Expiry Date</strong>
                  </TableCell>
                  <TableCell>
                    <strong>isActive</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {enterprises.map((enterprise) => (
                  <TableRow
                    key={enterprise.id}
                    hover
                    onClick={() => handleEnterpriseClick(enterprise.id)}
                  >
                    <TableCell>{enterprise.name || "N/A"}</TableCell>
                    <TableCell>{enterprise.capacity || "N/A"}</TableCell>
                    <TableCell>
                      {enterprise.expiryDate
                        ? enterprise.expiryDate.includes("T")
                          ? enterprise.expiryDate.split("T")[0]
                          : enterprise.expiryDate
                        : "N/A"}
                    </TableCell>
                    <TableCell>{enterprise.isActive ? "Yes" : "No"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}
      <Dialog
        open={createDialogOpen}
        onClose={handleCloseDialog}
        aria-labelledby="create-enterprise-dialog-title"
      >
        <DialogTitle id="create-enterprise-dialog-title">
          Create Enterprise
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Enterprise Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newEnterpriseName}
            onChange={(e) => setNewEnterpriseName(e.target.value)}
            InputLabelProps={{
              sx: {
                "&.Mui-focused": { color: "#000" },
              },
            }}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: " #49994C",
              },
            }}
            required
          />
          <TextField
            autoFocus
            fullWidth
            label="Capacity"
            type="number"
            margin="dense"
            variant="outlined"
            value={newEnterpriseCapacity}
            onChange={(e) => setNewEnterpriseCapacity(e.target.value)}
            InputLabelProps={{
              sx: {
                "&.Mui-focused": { color: "#000" },
              },
            }}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: " #49994C",
              },
            }}
            required
          />
          <TextField
            fullWidth
            label="Expiry Date"
            type="date"
            variant="outlined"
            size="small"
            margin="dense"
            value={newExpiryDate}
            onChange={(e) => setNewExpiryDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
              sx: {
                "&.Mui-focused": { color: "#000" },
              },
            }}
            inputProps={{
              min: new Date().toISOString().split("T")[0], // makes sure that we don't select previous dates
            }}
            sx={{
              mb: 2,
              backgroundColor: "#f5f5f5",
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: " #49994C",
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            sx={{
              color: " #49994C",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateEnterprise}
            variant="contained"
            disabled={!newEnterpriseName.trim() || creating}
            sx={{
              backgroundColor: " #49994C",
              "&:hover": { backgroundColor: "darkgreen" },
            }}
          >
            {creating ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default Enterprise;
